import { Alert, Box, MenuItem, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography } from '@mui/material';
import * as React from 'react';

import { useDeveloperApi } from './api';
import { DeveloperShell, ErrorAlert, LoadMore, Loading, mono, TableScroll, Timestamp, ToneChip } from './components';
import { VENDOR_CALL_OUTCOMES, VENDOR_CALL_REASONS, vendorCallOutcome } from './labels';
import type { VendorCall, VendorCallPage } from './types';
import { useCursorList } from './useCursorList';

/** The sandbox's vendor calls (SBX D12; S1): the reads it made, and the writes refused at the HTTP
 *  client so a sandbox never changes anything at the vendor. */
const VendorCallsPage = () => {
    const api = useDeveloperApi();
    const [outcome, setOutcome] = React.useState('');
    const list = useCursorList<VendorCall, VendorCallPage>(api.vendorCalls, { outcome });

    return (
        <DeveloperShell title="Vendor calls">
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Calls the sandbox made to your booking system. It reads; it never writes: a write is refused before it leaves.
                The last 2,000 calls of 14 days are kept.
            </Typography>
            {list.page?.shared_vendor_quota && (
                <Alert severity="info" sx={{ my: 1 }} data-testid="shared-vendor-quota">{list.page.shared_vendor_quota.text}</Alert>
            )}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, my: 1.5 }}>
                <TextField select size="small" label="Outcome" value={outcome} onChange={(ev) => setOutcome(ev.target.value)} sx={{ minWidth: 170 }}>
                    <MenuItem value="">Any</MenuItem>
                    {Object.entries(VENDOR_CALL_OUTCOMES).map(([value, { label }]) => <MenuItem key={value} value={value}>{label}</MenuItem>)}
                </TextField>
            </Box>
            {list.error ? <ErrorAlert error={list.error} onRetry={list.reload} /> : null}
            {list.loading && list.items.length === 0 ? <Loading /> : (
                <TableScroll>
                    <Table size="small" data-testid="vendor-calls">
                        <TableHead>
                            <TableRow>
                                <TableCell>When</TableCell>
                                <TableCell>Outcome</TableCell>
                                <TableCell>Vendor</TableCell>
                                <TableCell>Call</TableCell>
                                <TableCell>Purpose</TableCell>
                                <TableCell>Answer</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {list.items.map((row, idx) => {
                                const label = vendorCallOutcome(row.outcome);
                                return (
                                    <TableRow key={`${row.at}-${idx}`}>
                                        <TableCell><Timestamp value={row.at} /></TableCell>
                                        <TableCell>
                                            <ToneChip label={label.label} tone={label.tone} />
                                            {row.reason && (
                                                <Typography variant="caption" color="text.secondary" component="div">
                                                    {VENDOR_CALL_REASONS[row.reason] || row.reason}
                                                </Typography>
                                            )}
                                        </TableCell>
                                        <TableCell>{row.vendor}</TableCell>
                                        <TableCell sx={{ maxWidth: 360 }}><Typography sx={mono}>{row.method} {row.host}{row.path}</Typography></TableCell>
                                        <TableCell>{row.purpose || '—'}</TableCell>
                                        <TableCell>
                                            {row.status ?? (row.error || '—')}
                                            {row.duration_ms != null ? ` · ${row.duration_ms} ms` : ''}
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                            {!list.loading && list.items.length === 0 && !list.error && (
                                <TableRow><TableCell colSpan={6}><Typography variant="body2" color="text.secondary">No vendor calls.</Typography></TableCell></TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableScroll>
            )}
            <LoadMore hasMore={list.hasMore} loading={list.loading} onClick={list.loadMore} />
        </DeveloperShell>
    );
};

export default VendorCallsPage;
